/**
 * Approving a quote from the portal — §6.11's third screen.
 *
 * Option, add-ons, signature, in that order, because that is the order the
 * decision is made in: which of these, what else, and then yes. The tablet
 * flow asks the same three things and posts to the same service, so the
 * `quote_approvals` row is the same row (§6.11's acceptance test).
 *
 * Prices are shown, and that is deliberate: §G.6 carves out exactly this case
 * — "a contact with `approve_quotes` does see that quote's prices, options and
 * add-on amounts, because approving without the price is meaningless". It
 * still does not imply `view_invoices`, and nothing on this page is an
 * invoice.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card, Checkbox, EmptyState, SectionLabel } from '../components/primitives.js'
import { Field } from '../components/fields.js'
import { RowtedLogo } from '../frame/logo.js'
import { formatDollars } from '../lib/format.js'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import {
  ApiError,
  type InvoiceTerms, type MonthSplit, type ProposalPayment, type ProposalSelection, type ProposalView,
} from '@rowted/shared'
import { Proposal } from '../quote/quote-proposal.js'
import { PortalSignedOut } from './portal.js'

interface Payload {
  quote: {
    id: string; title: string; status: string; total: string; propertyName: string
    builder: string
  }
  /**
   * Present on a v2 quote: the customer's own document, projected on the
   * server. **Never the `QuoteTree`** — that is what used to be here, costs,
   * margins, crew notes and all (`docs/proposal.md` §1.1).
   */
  proposal: ProposalView | null
  options: { id: string; label: string; total: string }[]
  lines: {
    id: string; quoteOptionId: string | null; description: string; amount: string
    isAddOn: boolean
  }[]
}

export function PortalQuote() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<Payload | null>(null)
  const [option, setOption] = useState<string | null>(null)
  const [addOns, setAddOns] = useState<Set<string>>(new Set())
  /** The v2 answer to the same two questions the v1 UI asks above. */
  const [selection, setSelection] = useState<ProposalSelection | null>(null)
  const [signature, setSignature] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /* No portal session — the portal's own signed-out screen, not a server error. */
  const [signedOut, setSignedOut] = useState(false)

  const load = useCallback(async () => {
    try {
      const payload = await request<Payload>(`/portal/quotes/${id}`)
      setData(payload)
      setOption(payload.options[0]?.id ?? null)
      /*
       * The opening selection comes from the server, so the page a customer
       * opens and the selection `approve` validates against are the same
       * object rather than two implementations of §7.1's rule.
       */
      if (payload.proposal) setSelection(payload.proposal.initial)
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'unauthenticated') setSignedOut(true)
      setError(cause instanceof Error ? cause.message : 'That quote is not available.')
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  /*
   * Back from the thank-you to the document, as it now is: approved, with
   * `view.signed` filled, so the page draws the signed copy rather than the
   * sign card. It re-reads rather than keeping what it had, because what was
   * on screen was the *offer* and what this shows is the signature.
   */
  const reopen = useCallback(async () => {
    await load()
    setDone(false)
  }, [load])

  async function approve() {
    if (!option) return
    setBusy(true)
    setError(null)
    try {
      await request(`/portal/quotes/${id}/approve`, {
        method: 'POST',
        body: {
          quoteOptionId: option,
          addOnLineIds: [...addOns],
          signatureName: signature.trim(),
        },
      })
      setDone(true)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be approved.')
    } finally {
      setBusy(false)
    }
  }

  const frame = (children: React.ReactNode) => (
    <div className="min-h-app bg-bg">
      <header className="flex h-[60px] items-center border-b border-line bg-surface px-4">
        <RowtedLogo tenantName={null} />
      </header>
      <main className="mx-auto max-w-[680px] px-4 py-4">{children}</main>
      <footer className="px-4 pb-8 pt-2 text-center text-sub text-text-faint">
        Powered by Rowted
      </footer>
    </div>
  )

  if (signedOut && !data) return <PortalSignedOut />
  if (error && !data) return frame(<EmptyState title="Not available" body={error} />)
  if (!data) return frame(<p className="m-0 text-body text-text-muted">Loading…</p>)

  if (done) {
    /*
     * **The thank-you, in the company's own words** (§9.2).
     *
     * "We will be in touch" is a promise only the company can make, so the
     * lines under the heading are `nextSteps` from their own settings and
     * there is no generic sentence standing in for them (rule 12). A deposit
     * says its figure where there is one, because "what happens about money"
     * is the question a customer holds right after signing.
     */
    const steps = data.proposal?.nextSteps ?? []
    const deposit = data.proposal?.signed?.deposit ?? null

    return frame(
      <Card className="rounded-[14px] p-5">
        <p className="m-0 text-[20px] font-bold text-text">Thank you — that is approved.</p>
        {steps.length > 0 ? (
          <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
            {steps.map((step) => (
              <li key={step} className="flex items-start gap-2.5 text-body text-text-body">
                <span aria-hidden="true" className="mt-0.5 shrink-0 text-accent">✓</span>
                {step}
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 mt-2 text-body text-text-muted">
            Nothing else is needed from you right now.
          </p>
        )}
        {deposit && (
          <p className="tnum m-0 mt-3 rounded-field bg-accent-soft px-3 py-2.5 text-body
                        text-text-body">
            {formatDollars(deposit.amount)} is due now. We will email the invoice.
          </p>
        )}
        <Button className="mt-4 h-11 w-full" onClick={() => void reopen()}>
          See what you signed
        </Button>
        <Button
          variant="ghost"
          className="mt-2 h-11 w-full"
          onClick={() => navigate('/portal')}
        >
          Back to your places
        </Button>
      </Card>,
    )
  }

  /*
   * A v2 quote is the same document the office previewed, rendered by the
   * same component. Everything below this point is the old builder's page and
   * goes when it does.
   */
  if (data.proposal) {
    const view = data.proposal
    const picked = selection ?? view.initial
    /*
     * **No `frame()` here** (`docs/proposal.md` §5.1). The portal frame draws
     * `RowtedLogo` above every page and caps the content at 680px; on this one
     * page that is our brand on a document somebody is about to sign with
     * their landscaper, inside a column too narrow for the two-column
     * document. `ProposalPage` carries the company's own header, the cover
     * band and its own width, and "Proposals by Rowted" stays as one grey
     * line in its footer.
     */
    return (
      <Proposal
        view={view}
        selection={picked}
        onSelection={setSelection}
        busy={busy}
        error={error}
        /*
         * The payment schedule for what is ticked right now (§4.4, §12.1).
         *
         * The page adds the money up itself — `selectionTotals` and
         * `monthSplit`, both pure and both instant — but the invoices come out
         * of the billing plans, which the customer's browser never receives,
         * so the schedule is a round trip. `usePrice` debounces it and drops
         * a late answer to a superseded question.
         */
        onPrice={async (selection) => request<{
          payments: ProposalPayment[]; month: MonthSplit | null; stale: boolean
          invoiceTerms: InvoiceTerms | null
        }>(`/portal/quotes/${id}/price`, {
          method: 'POST',
          body: { selection, rev: view.rev },
        })}
        onApprove={async (signature) => {
          setBusy(true)
          setError(null)
          try {
            /*
             * **The whole of §8.3**: what was chosen, what the page was
             * showing when they chose it (`rev`), how they signed, that they
             * agreed, and which terms were in front of them. The server
             * refuses a stale `rev` or a stale `termsId` rather than
             * recording a signature against a document that has moved.
             */
            await request(`/portal/quotes/${id}/approve`, {
              method: 'POST',
              body: {
                selection: picked,
                rev: view.rev,
                signature,
                agreed: true,
                termsId: view.terms?.id ?? null,
              },
            })
            setDone(true)
          } catch (cause) {
            setError(cause instanceof Error ? cause.message : 'That could not be approved.')
          } finally {
            setBusy(false)
          }
        }}
        /*
         * **Not every answer is yes** (§9.4). The three links under the sign
         * card and the one action a closed page offers, all four of them
         * writing a `quote_responses` row and telling the estimator. Only the
         * portal passes these: on the office's own preview a Decline link is
         * a control that cannot mean what it says.
         *
         * Each re-reads the page afterwards, because a decline has changed
         * which page this is and a question has not — and working that out in
         * the browser is exactly what `view.state` exists to avoid.
         */
        responses={{
          question: async (body) => {
            await request(`/portal/quotes/${id}/question`, { method: 'POST', body: { body } })
          },
          changes: async (refs, body) => {
            await request(`/portal/quotes/${id}/changes`,
              { method: 'POST', body: { refs, body } })
          },
          decline: async (reason, note) => {
            await request(`/portal/quotes/${id}/decline`,
              { method: 'POST', body: { reason, note } })
            await load()
          },
          reopen: async (note) => {
            await request(`/portal/quotes/${id}/reopen`, { method: 'POST', body: { note } })
          },
        }}
      />
    )
  }

  const chosen = data.lines.filter(
    (line) => !line.isAddOn && (line.quoteOptionId === option || line.quoteOptionId === null))
  const extras = data.lines.filter((line) => line.isAddOn)
  const total = chosen.reduce((sum, line) => sum + Number(line.amount), 0)
    + extras.filter((line) => addOns.has(line.id))
      .reduce((sum, line) => sum + Number(line.amount), 0)

  return frame(
    <>
      <div className="mb-3">
        <h1 className="m-0 text-[20px] font-bold text-text">{data.quote.title}</h1>
        <p className="m-0 text-body text-text-muted">{data.quote.propertyName}</p>
      </div>

      {data.options.length > 1 && (
        <section className="mb-4">
          <SectionLabel className="mb-2 block">Choose one</SectionLabel>
          <div className="flex flex-col gap-2">
            {data.options.map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => setOption(entry.id)}
                className={cn(
                  'flex items-center gap-3 rounded-[14px] border p-3.5 text-left',
                  option === entry.id
                    ? 'border-accent-line bg-accent-soft ring-2 ring-accent-ring'
                    : 'border-line bg-surface',
                )}
              >
                <span className="min-w-0 flex-1 text-[16px] font-bold text-text">
                  {entry.label}
                </span>
                <span className="tnum shrink-0 text-body text-text">
                  {formatDollars(Number(entry.total))}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="mb-4">
        <SectionLabel className="mb-2 block">What is included</SectionLabel>
        <Card className="rounded-[14px] p-0">
          <ul className="m-0 list-none p-0">
            {chosen.map((line) => (
              <li key={line.id}
                className="flex items-baseline gap-3 border-b border-line-soft px-3.5 py-3 last:border-b-0">
                <span className="min-w-0 flex-1 text-body text-text">{line.description}</span>
                <span className="tnum shrink-0 text-body text-text">
                  {formatDollars(Number(line.amount))}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </section>

      {extras.length > 0 && (
        <section className="mb-4">
          <SectionLabel className="mb-2 block">Anything else?</SectionLabel>
          <Card className="rounded-[14px] p-0">
            <ul className="m-0 list-none p-0">
              {extras.map((line) => (
                <li key={line.id}
                  className="flex items-center gap-3 border-b border-line-soft px-3.5 py-3 last:border-b-0">
                  <Checkbox
                    checked={addOns.has(line.id)}
                    onChange={() => setAddOns((current) => {
                      const next = new Set(current)
                      if (next.has(line.id)) next.delete(line.id)
                      else next.add(line.id)
                      return next
                    })}
                    label={line.description}
                  />
                  <span className="tnum ml-auto shrink-0 text-body text-text">
                    {formatDollars(Number(line.amount))}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}

      <Card className="rounded-[14px] p-3.5">
        <div className="mb-3 flex items-baseline justify-between">
          <span className="text-[16px] font-bold text-text">Total</span>
          <span className="tnum text-[18px] font-bold text-text">{formatDollars(total)}</span>
        </div>

        {error && <p className="m-0 mb-2 text-body text-danger">{error}</p>}

        {/*
          * The signature. §6.11: "approve = option + add-ons + signature".
          * A typed name, which is what the tablet takes too — the same
          * `signature_name` lands on the same `quote_approvals` row.
          */}
        <Field
          label="Type your name to approve"
          value={signature}
          onChange={setSignature}
          placeholder="Rita Alvarez"
          required
        />

        <Button
          variant="primary"
          className="mt-3 h-12 w-full"
          disabled={busy || !option || signature.trim().length < 2}
          onClick={() => void approve()}
        >
          {busy ? 'Approving…' : `Approve ${formatDollars(total)}`}
        </Button>
        <p className="m-0 mt-2 text-sub text-text-muted">
          Approving books the work in. We will confirm the dates with you before
          anybody turns up.
        </p>
      </Card>
    </>,
  )
}
